import { createFileRoute, Link } from '@tanstack/react-router'
import QRCode from 'react-qr-code'
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'

const POLL_INTERVAL_MS = 4000
const FINAL = new Set(['paid', 'expired', 'canceled'])

export const Route = createFileRoute('/tips')({
  head: () => ({
    meta: [
      { title: 'tips - jfrader' },
      { name: 'description', content: 'tip jfrader with bitcoin or lightning.' },
      { property: 'og:title', content: 'tips - jfrader' },
      { property: 'og:description', content: 'tip jfrader with bitcoin or lightning.' },
      { property: 'og:url', content: 'https://jfrader.com/tips' },
    ],
    links: [
      { rel: 'canonical', href: 'https://jfrader.com/tips' }
    ]
  }),
  component: TipsPage,
})

// The payment panel already says "Waiting for payment", so the message field
// stays quiet until it actually sends.
function tipMessageStatus(state: 'idle' | 'sending' | 'sent' | 'error', error: string) {
  if (state === 'sending') return 'sending'
  if (state === 'sent') return 'sent'
  if (state === 'error') return error
  return ''
}

function tipMessageStatusClass(state: 'idle' | 'sending' | 'sent' | 'error') {
  if (state === 'sent') return 'form__status form__status--ok'
  if (state === 'error') return 'form__status form__status--err'
  return 'form__status'
}

function TipsPage() {
  const [active, setActive] = useState<'bitcoin' | 'lightning'>('bitcoin')

  // Lightning
  const [invoice, setInvoice] = useState<any>(null)
  const [invStatus, setInvStatus] = useState<'idle'|'pending'|'paid'|'expired'|'canceled'>('idle')
  const [invErr, setInvErr] = useState('')
  const [invLoading, setInvLoading] = useState(false)
  const invReqId = useRef(0)

  // Bitcoin
  const [addr, setAddr] = useState<any>(null)
  const [addrStatus, setAddrStatus] = useState<'idle'|'pending'|'paid'|'unavailable'>('idle')
  const [addrErr, setAddrErr] = useState('')
  const [addrLoading, setAddrLoading] = useState(false)
  const addrReqId = useRef(0)

  // Message is drafted, then explicitly saved; the saved copy is what sends
  const [draft, setDraft] = useState({ email: '', message: '', name: '' })
  const [saved, setSaved] = useState(false)
  const savedRef = useRef({ email: '', message: '', name: '' })
  const [msgState, setMsgState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  const [msgError, setMsgError] = useState('')
  const sentRefs = useRef<Set<string>>(new Set())
  const sendingRef = useRef(false)
  const honeypotRef = useRef<HTMLInputElement>(null)
  const [paidAmount, setPaidAmount] = useState<number | null>(null)

  const isLightning = active === 'lightning'
  const payment = isLightning ? {
    hint: 'Lightning Invoice',
    value: invoice?.paymentRequest || '',
    status: invStatus,
    err: invErr,
    loading: invLoading,
    isPaid: invStatus === 'paid',
  } : {
    hint: 'On-chain Address',
    value: addr?.address || '',
    status: addrStatus,
    err: addrErr,
    loading: addrLoading,
    isPaid: addrStatus === 'paid',
  }
  const reference = isLightning ? invoice?.paymentHash || '' : addr?.address || ''

  const generateLightning = useCallback(async () => {
    const id = ++invReqId.current
    setInvLoading(true); setInvErr('')
    try {
      const r = await fetch('/api/v1/tip', { method: 'POST', headers: { 'Content-Type': 'application/json' } })
      const b = await r.json().catch(() => null)
      if (!r.ok || !b?.paymentRequest) throw new Error(b?.error || 'No invoice')
      if (id !== invReqId.current) return
      setInvoice(b)
      setInvStatus('pending')
      setPaidAmount(null)
    } catch (e: any) {
      if (id === invReqId.current) setInvErr(e.message || 'Unable to generate')
    } finally {
      if (id === invReqId.current) setInvLoading(false)
    }
  }, [])

  const generateBitcoin = useCallback(async () => {
    const id = ++addrReqId.current
    setAddrLoading(true); setAddrErr('')
    try {
      const r = await fetch('/api/v1/tip/address', { method: 'POST', headers: { 'Content-Type': 'application/json' } })
      const b = await r.json().catch(() => null)
      if (!r.ok || !b?.address) throw new Error(b?.error || 'No address')
      if (id !== addrReqId.current) return
      setAddr(b)
      setAddrStatus('pending')
      setPaidAmount(null)
    } catch (e: any) {
      if (id === addrReqId.current) {
        setAddrErr(e.message || 'Unable')
        setAddrStatus('unavailable')
      }
    } finally {
      if (id === addrReqId.current) setAddrLoading(false)
    }
  }, [])

  // auto start lightning. `!invErr` matters: on failure the status stays idle
  // and the invoice stays null, so without it this effect retries forever.
  useEffect(() => {
    if (active === 'lightning' && invStatus === 'idle' && !invLoading && !invoice && !invErr) {
      generateLightning()
    }
  }, [active, generateLightning, invLoading, invStatus, invoice, invErr])

  // auto start bitcoin
  useEffect(() => {
    if (active === 'bitcoin' && addrStatus === 'idle' && !addrLoading && !addr) {
      generateBitcoin()
    }
  }, [active, generateBitcoin, addrLoading, addrStatus, addr])

  // polling lightning
  useEffect(() => {
    if (active !== 'lightning' || invStatus !== 'pending' || !invoice?.paymentHash) return
    let cancel = false
    const ctrl = new AbortController()
    const poll = async () => {
      try {
        const r = await fetch(`/api/v1/tip/status?paymentHash=${invoice.paymentHash}`, { signal: ctrl.signal })
        const b = await r.json()
        if (!cancel && b.status) {
          const s = FINAL.has(b.status) ? b.status : 'pending'
          setInvStatus(s as any)
          if (s === 'paid' && typeof b.amountSat === 'number') setPaidAmount(b.amountSat)
        }
      } catch {}
    }
    poll()
    const iv = setInterval(poll, POLL_INTERVAL_MS)
    return () => { cancel = true; ctrl.abort(); clearInterval(iv) }
  }, [active, invStatus, invoice])

  // polling bitcoin
  useEffect(() => {
    if (active !== 'bitcoin' || addrStatus !== 'pending' || !addr?.address) return
    let cancel = false
    const ctrl = new AbortController()
    const poll = async () => {
      try {
        const r = await fetch(`/api/v1/tip/address/status?address=${encodeURIComponent(addr.address)}`, { signal: ctrl.signal })
        const b = await r.json()
        if (!cancel && b.status) {
          setAddrStatus(b.status)
          if (b.status === 'paid' && typeof b.amountSat === 'number') setPaidAmount(b.amountSat)
        }
      } catch {}
    }
    poll()
    const iv = setInterval(poll, POLL_INTERVAL_MS)
    return () => { cancel = true; ctrl.abort(); clearInterval(iv) }
  }, [active, addrStatus, addr])

  const sendTipMessage = useCallback(async () => {
    const snapshot = savedRef.current
    const message = snapshot.message.trim()
    if (!message || !reference || !payment.isPaid || sendingRef.current) return
    if (sentRefs.current.has(reference)) return

    sendingRef.current = true
    setMsgState('sending')
    setMsgError('')

    try {
      const response = await fetch('/api/v1/tip/message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          method: active,
          ...(isLightning ? { paymentHash: reference } : { address: reference }),
          name: snapshot.name,
          email: snapshot.email,
          message,
          website: honeypotRef.current?.value || '',
        }),
      })

      if (response.status === 402) throw new Error('payment not settled yet')
      if (response.status === 409) throw new Error('message already sent for this tip')
      if (response.status === 429) throw new Error('too many messages, try again later')
      if (!response.ok) throw new Error('could not send, try again')

      sentRefs.current.add(reference)
      setMsgState('sent')
    } catch (error) {
      setMsgError(error instanceof Error ? error.message : 'could not send, try again')
      setMsgState('error')
    } finally {
      sendingRef.current = false
    }
  }, [active, isLightning, payment.isPaid, reference])

  // Send the drafted message the moment the tip shown on the page settles.
  useEffect(() => {
    if (payment.isPaid) void sendTipMessage()
  }, [payment.isPaid, sendTipMessage])

  // A fresh invoice or address starts a fresh message.
  useEffect(() => {
    setMsgState('idle')
    setMsgError('')
  }, [reference])

  function updateDraft(patch: Partial<typeof draft>) {
    setDraft((current) => ({ ...current, ...patch }))
    setSaved(false)
  }

  function saveMessage() {
    savedRef.current = { ...draft }
    setSaved(true)
  }

  function handleMessageSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    saveMessage()
  }

  // after a settled tip: new invoice or address, empty draft
  function resetTip() {
    setPaidAmount(null)
    setMsgState('idle')
    setMsgError('')
    setDraft({ email: '', message: '', name: '' })
    savedRef.current = { email: '', message: '', name: '' }
    setSaved(false)
    if (isLightning) {
      setInvoice(null)
      setInvStatus('idle')
      setInvErr('')
    } else {
      setAddr(null)
      setAddrStatus('idle')
      setAddrErr('')
    }
  }

  const qr = isLightning ? (invoice?.paymentRequest ? `lightning:${invoice.paymentRequest}` : '') : (addr?.address ? `bitcoin:${addr.address}` : '')
  const messageStatusText = tipMessageStatus(msgState, msgError)

  return (
    <section className="sec">
      <h2 className="sec__label">tips</h2>

      <div className="pay__tabs" role="group" aria-label="Payment method">
        {(['bitcoin', 'lightning'] as const).map((m) => (
          <button
            key={m}
            type="button"
            aria-pressed={active === m}
            className={active === m ? 'pay__tab pay__tab--on' : 'pay__tab'}
            onClick={() => setActive(m)}
          >
            {m}
          </button>
        ))}
      </div>

      <div className="pay rv">
        <div className="pay__qr">
          {payment.isPaid ? (
            <div className="receipt">
              <span className="receipt__amount">
                {paidAmount != null ? paidAmount.toLocaleString() : 'paid'}
              </span>
              <span className="receipt__unit">
                {paidAmount != null ? 'sats received' : 'received'}
              </span>
            </div>
          ) : qr ? (
            <QRCode value={qr} size={200} />
          ) : (
            <span>{payment.loading ? 'Loading…' : payment.err || 'No address'}</span>
          )}
        </div>

        <div>
          <p className="pay__hint">{payment.hint}</p>
          <div className="pay__value">{payment.value || '…'}</div>

          <div className={payment.isPaid ? 'pay__status pay__status--paid' : 'pay__status'}>
            {payment.isPaid ? 'Thank you!' : payment.err || (payment.status === 'pending' ? 'Waiting for payment' : 'Ready')}
          </div>

          <div className="pay__actions">
            {payment.value && !payment.isPaid && (
              <a
                className="pay__btn pay__btn--accent"
                href={isLightning ? `lightning:${payment.value}` : `bitcoin:${payment.value}`}
                target="_blank"
                rel="noreferrer"
              >
                Open wallet
              </a>
            )}
            {isLightning && (invStatus === 'expired' || invStatus === 'canceled' || !!invErr) && (
              <button type="button" className="pay__btn" onClick={generateLightning} disabled={invLoading}>
                New invoice
              </button>
            )}
            {!isLightning && !!addrErr && (
              <button type="button" className="pay__btn" onClick={generateBitcoin} disabled={addrLoading}>
                New address
              </button>
            )}
            {payment.value && !payment.isPaid && (
              <button
                type="button"
                className="pay__btn"
                onClick={() => {
                  navigator.clipboard?.writeText(payment.value)
                }}
              >
                Copy
              </button>
            )}
          </div>
        </div>
      </div>

      {payment.isPaid ? (
        <div className="form form--tip">
          <div className="form__actions">
            <button type="button" className="pay__btn pay__btn--accent" onClick={resetTip}>
              Tip again
            </button>
            <Link to="/" className="pay__btn">Home</Link>
            {msgState === 'sending' && (
              <span className="form__status" role="status">sending message</span>
            )}
            {msgState === 'sent' && (
              <span className="form__status form__status--ok" role="status">message sent</span>
            )}
            {msgState === 'error' && (
              <>
                <span className="form__status form__status--err" role="status">{msgError}</span>
                <button type="button" className="pay__btn" onClick={() => void sendTipMessage()}>
                  retry
                </button>
              </>
            )}
          </div>
        </div>
      ) : (
        <form className="form form--tip" onSubmit={handleMessageSubmit}>
          <div className="field">
            <label htmlFor="tip-message">message</label>
            <textarea
              id="tip-message"
              value={draft.message}
              onChange={(event) => updateDraft({ message: event.target.value })}
              rows={3}
              maxLength={4000}
            />
          </div>
          <div className="form__row">
            <div className="field">
              <label htmlFor="tip-name">name</label>
              <input
                id="tip-name"
                type="text"
                autoComplete="name"
                maxLength={120}
                value={draft.name}
                onChange={(event) => updateDraft({ name: event.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="tip-email">email</label>
              <input
                id="tip-email"
                type="email"
                autoComplete="email"
                maxLength={254}
                value={draft.email}
                onChange={(event) => updateDraft({ email: event.target.value })}
              />
            </div>
          </div>
          <input ref={honeypotRef} className="hp" type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" />
          <div className="form__actions">
            <button type="submit" className="pay__btn pay__btn--accent" disabled={saved}>
              {saved ? 'Saved' : 'Save message'}
            </button>
            {messageStatusText && msgState === 'error' && (
              <span className={tipMessageStatusClass(msgState)} role="status">{messageStatusText}</span>
            )}
          </div>
        </form>
      )}
    </section>
  )
}
